import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useCountUp } from "../hooks/useCountUp";
import api from "../lib/api";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function Barometro() {
  const [groupby, setGroupby] = useState("barrio");
  const [elemento, setElemento] = useState("__todos__");
  const [elementos, setElementos] = useState([]);
  const [data, setData] = useState(null);
  const [activeGrupo, setActiveGrupo] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    const filtro = elemento === "__todos__" ? "" : `&elemento=${encodeURIComponent(elemento)}`;
    api.get(`/iris/barometro?groupby=${groupby}${filtro}`).then((r) => {
      setData(r.data);
      setActiveGrupo(r.data.ranking_zonas_calientes?.[0]?.grupo || null);
    });
  };

  useEffect(() => {
    api.get("/iris/elementos").then((r) => setElementos(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [groupby, elemento]);

  const serie = useMemo(() => {
    if (!data || !activeGrupo) return [];
    return (data.series[activeGrupo] || []).sort((a, b) => a.periodo.localeCompare(b.periodo));
  }, [data, activeGrupo]);

  const totalActivo = serie.reduce((s, p) => s + p.total, 0);
  const animatedTotal = useCountUp(totalActivo);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/iris/sync");
      if (data.ok) toast.success(`Sincronización completada: ${data.fichas_nuevas} fichas nuevas`);
      else toast.warning(`Conector A en modo pendiente/degradado: ${data.error}`);
    } finally {
      setSyncing(false);
      load();
    }
  };

  return (
    <AppLayout title="Barómetro IRIS">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-3">
          <Select value={groupby} onValueChange={setGroupby}>
            <SelectTrigger className="w-56" data-testid="barometro-groupby-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="barrio">Agrupar por barrio</SelectItem>
              <SelectItem value="tematica">Agrupar por temática</SelectItem>
            </SelectContent>
          </Select>
          <Select value={elemento} onValueChange={setElemento}>
            <SelectTrigger className="w-64" data-testid="barometro-elemento-select">
              <SelectValue placeholder="Filtrar por elemento real" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__todos__">Todos los elementos IRIS</SelectItem>
              {elementos.map((e) => (
                <SelectItem key={e} value={e}>
                  {e}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="sync-opendata-button" variant="outline" onClick={sync} disabled={syncing} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          Sincronizar Open Data BCN ahora
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">
              Tendencia trimestral — <span className="text-valoriza-leaf">{activeGrupo}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-5xl font-black tracking-tighter mb-4" data-testid="barometro-total-activo">
              {animatedTotal}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={serie} onMouseMove={(e) => e?.activeLabel}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#7AB800" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">Ranking de zonas calientes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1" data-testid="ranking-zonas-calientes">
            {data?.ranking_zonas_calientes?.map((r, i) => (
              <button
                key={r.grupo}
                onClick={() => setActiveGrupo(r.grupo)}
                data-testid={`ranking-item-${i}`}
                className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  activeGrupo === r.grupo ? "bg-valoriza-leaf text-black font-bold" : "hover:bg-secondary"
                }`}
              >
                <span>
                  {i + 1}. {r.grupo}
                </span>
                <span className="font-bold">{r.total}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
